/**
 * Custom Pagination Component
 *
 * A reusable pagination component that provides navigation between pages of data.
 * It shows current page, total pages, and navigation controls.
 *
 * Features:
 * - Previous/Next navigation buttons
 * - Current page indicator
 * - Results count display
 * - Disabled state for buttons when at first/last page
 * - Internationalization support
 *
 * @module components/pagination/custom-pagination
 */

import {
  faChevronLeft,
  faChevronRight
} from "@fortawesome/free-solid-svg-icons";
import { ListGroup } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";

/**
 * Custom pagination component with navigation controls
 *
 * @param {Object} props - Component properties
 * @param {Object} props.paginateOpts - Pagination options
 * @param {number} props.paginateOpts.totalPages - Total number of pages
 * @param {boolean} props.paginateOpts.hasPrevPage - Whether previous page is available
 * @param {boolean} props.paginateOpts.hasNextPage - Whether next page is available
 * @param {number} props.paginateOpts.totalDocs - Total number of items across all pages
 * @param {number} props.paginateOpts.rowsOnPage - Number of items on current page
 * @param {number} props.activeTab - Current active page number
 * @param {Function} props.select - Function to call when page is changed
 * @returns {JSX.Element} Pagination component
 */
const CustomPagination = ({
  paginateOpts: { totalPages, hasPrevPage, hasNextPage, totalDocs, rowsOnPage },
  activeTab,
  select
}) => {
  const { t } = useTranslation();

  /**
   * Handle page selection
   * Only selects the page if it's different from the current active page
   *
   * @param {number} tabIndex - Page number to select
   */
  const onSelect = (tabIndex) => {
    if (activeTab !== tabIndex) {
      select(tabIndex);
    }
  };

  /**
   * Render pagination controls and results counter
   */
  return (
    <div className="d-flex align-items-center justify-content-between mb-2">
      {/* Pagination controls */}
      <ListGroup className="fw-bold border" horizontal>
        {/* Previous page button */}
        <ListGroup.Item
          role="button"
          className={`border-0 border-end ${hasPrevPage && "text-primary"}`}
          onClick={() => onSelect(activeTab - 1)}
          disabled={!hasPrevPage}
        >
          <FontAwesomeIcon icon={faChevronLeft} />
        </ListGroup.Item>

        {/* Page indicator */}
        <ListGroup.Item className="border-0">
          {activeTab} <span className="mx-2 fw-normal">{t("common.of")}</span>
          {totalPages > 0 ? totalPages : activeTab}
        </ListGroup.Item>

        {/* Next page button */}
        <ListGroup.Item
          role="button"
          className={`border-0 border-start ${hasNextPage && "text-primary"}`}
          onClick={() => onSelect(activeTab + 1)}
          disabled={!hasNextPage}
        >
          <FontAwesomeIcon icon={faChevronRight} />
        </ListGroup.Item>
      </ListGroup>

      {/* Results counter */}
      <div>
        {rowsOnPage} {t("common.of")} {totalDocs} {t("common.results")}
      </div>
    </div>
  );
};

export default CustomPagination;
